package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RmpApproval
import br.com.vvrefeicoes.portal.domain.model.RmpApprovalEntry
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewItem
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.ClipboardCheck
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.ListChecks
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Printer
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.Upload
import com.composables.icons.lucide.X
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_approval_approve
import portalvv.shared.generated.resources.rmp_approval_approved
import portalvv.shared.generated.resources.rmp_approval_attachments
import portalvv.shared.generated.resources.rmp_approval_delete
import portalvv.shared.generated.resources.rmp_approval_delete_confirm
import portalvv.shared.generated.resources.rmp_approval_edit
import portalvv.shared.generated.resources.rmp_approval_fields
import portalvv.shared.generated.resources.rmp_approval_history
import portalvv.shared.generated.resources.rmp_approval_note
import portalvv.shared.generated.resources.rmp_approval_reject
import portalvv.shared.generated.resources.rmp_approval_rejected
import portalvv.shared.generated.resources.rmp_approval_title
import portalvv.shared.generated.resources.rmp_attach_documents
import portalvv.shared.generated.resources.rmp_checklist_complete
import portalvv.shared.generated.resources.rmp_checklist_fill
import portalvv.shared.generated.resources.rmp_checklist_filled_by
import portalvv.shared.generated.resources.rmp_checklist_incomplete
import portalvv.shared.generated.resources.rmp_checklist_observation
import portalvv.shared.generated.resources.rmp_checklist_section
import portalvv.shared.generated.resources.rmp_col_requester
import portalvv.shared.generated.resources.rmp_col_status
import portalvv.shared.generated.resources.rmp_col_type
import portalvv.shared.generated.resources.rmp_col_unit
import portalvv.shared.generated.resources.rmp_col_waiting
import portalvv.shared.generated.resources.rmp_decision_approve_title
import portalvv.shared.generated.resources.rmp_decision_cancel
import portalvv.shared.generated.resources.rmp_decision_reject_title
import portalvv.shared.generated.resources.rmp_decision_summary
import portalvv.shared.generated.resources.rmp_document
import portalvv.shared.generated.resources.rmp_evaluation_done
import portalvv.shared.generated.resources.rmp_evaluation_fill
import portalvv.shared.generated.resources.rmp_evaluation_opinion
import portalvv.shared.generated.resources.rmp_evaluation_pending
import portalvv.shared.generated.resources.rmp_evaluation_section
import portalvv.shared.generated.resources.rmp_missing_fields
import portalvv.shared.generated.resources.rmp_missing_fields_ask
import portalvv.shared.generated.resources.rmp_missing_fields_edit
import portalvv.shared.generated.resources.rmp_required_actions
import portalvv.shared.generated.resources.rmp_required_actions_who
import portalvv.shared.generated.resources.rmp_required_actions_you

private const val NO_VALUE = "-"

/**
 * RMP aberto no portal: campos preenchidos, anexos, etapa atual e histórico. Para quem pode decidir a etapa atual,
 * aparecem a observação/motivo e os botões Aprovar e Rejeitar; para quem pode, Editar ([onEdit] abre o formulário)
 * e Excluir (com confirmação). [onDecided] recarrega a lista depois da decisão ou da exclusão.
 */
@Composable
internal fun RmpApprovalDialog(
    item: RmpOverviewItem,
    onDismiss: () -> Unit,
    onDecided: () -> Unit,
    onEdit: (String) -> Unit,
) {
    val controller = rememberRmpApprovalController()
    var confirmingDelete by remember(item.id) { mutableStateOf(false) }
    var showChecklist by remember(item.id) { mutableStateOf(false) }
    var showEvaluation by remember(item.id) { mutableStateOf(false) }
    LaunchedEffect(item.id) {
        controller.load(item.id)
    }
    AppModalCard(
        title = stringResource(Res.string.rmp_approval_title, item.code),
        onDismiss = onDismiss,
    ) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        val approval = controller.approval
        if (approval == null) {
            if (controller.loading) {
                Box(modifier = Modifier.fillMaxWidth().padding(AppSpacing.space24), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            }
            return@AppModalCard
        }
        Summary(item, approval)
        AppButton(
            text = stringResource(Res.string.rmp_document),
            onClick = controller::openDocument,
            variant = PortalButtonVariant.Text,
            leadingIcon = Lucide.Printer,
        )
        val checklist = approval.transferChecklist
        // Na transferência, o que falta costuma ser o checklist: o botão abre o checklist em vez do formulário.
        MissingFieldsNotice(
            approval,
            onAttach = { if (checklist != null && !checklist.complete) showChecklist = true else onEdit(item.id) },
        )
        if (checklist != null) {
            Section(stringResource(Res.string.rmp_checklist_section))
            Text(
                text =
                    stringResource(
                        if (checklist.complete) Res.string.rmp_checklist_complete else Res.string.rmp_checklist_incomplete,
                    ),
                style = MaterialTheme.typography.bodyMedium,
                color = if (checklist.complete) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
            )
            checklist.items.forEach { entry -> DetailRow(entry.label, entry.value ?: NO_VALUE) }
            checklist.observation?.let { DetailRow(stringResource(Res.string.rmp_checklist_observation), it) }
            checklist.filledByName?.let { name ->
                Text(
                    text = stringResource(Res.string.rmp_checklist_filled_by, name, formatBusinessDateTime(checklist.filledAt).orEmpty()),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            if (approval.canEdit) {
                AppButton(
                    text = stringResource(Res.string.rmp_checklist_fill),
                    onClick = { showChecklist = true },
                    enabled = !controller.deciding,
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.ListChecks,
                )
            }
            if (showChecklist) {
                RmpTransferChecklistDialog(
                    approval = approval,
                    checklist = checklist,
                    controller = controller,
                    onDismiss = { showChecklist = false },
                    onSaved = {
                        showChecklist = false
                        onDecided()
                    },
                )
            }
        }
        approval.testAttempts?.let { attempts ->
            HorizontalDivider()
            RmpTestSection(approval, attempts, controller, onChanged = onDecided)
        }
        approval.evaluation?.let { evaluation ->
            Section(stringResource(Res.string.rmp_evaluation_section))
            Text(
                text =
                    if (evaluation.done) {
                        stringResource(
                            Res.string.rmp_evaluation_done,
                            evaluationAverage(evaluation.criteria.mapNotNull { it.score?.toString() }),
                            evaluation.evaluatorName ?: NO_VALUE,
                            formatBusinessDateTime(evaluation.updatedAt) ?: NO_VALUE,
                        )
                    } else {
                        stringResource(Res.string.rmp_evaluation_pending)
                    },
                style = MaterialTheme.typography.bodyMedium,
                color = if (evaluation.done) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
            )
            evaluation.data["parecer_avaliacao"]?.takeIf { evaluation.done }?.let {
                DetailRow(stringResource(Res.string.rmp_evaluation_opinion), it)
            }
            if (approval.canEvaluate) {
                AppButton(
                    text = stringResource(Res.string.rmp_evaluation_fill),
                    onClick = { showEvaluation = true },
                    enabled = !controller.deciding,
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.ClipboardCheck,
                )
            }
            if (showEvaluation) {
                RmpEvaluationDialog(
                    evaluation = evaluation,
                    number = item.code,
                    controller = controller,
                    onDismiss = { showEvaluation = false },
                    onSaved = {
                        showEvaluation = false
                        onDecided()
                    },
                )
            }
        }
        Section(stringResource(Res.string.rmp_approval_fields))
        approval.fields.forEach { field ->
            if (field.section) {
                Text(text = field.label, style = MaterialTheme.typography.titleSmall)
            } else {
                DetailRow(field.label, field.value ?: NO_VALUE)
            }
        }
        if (approval.request.attachments.isNotEmpty()) {
            Section(stringResource(Res.string.rmp_approval_attachments))
            approval.request.attachments.forEach { attachment ->
                AppButton(
                    text = attachment.fileName,
                    onClick = { controller.downloadAttachment(attachment) },
                    variant = PortalButtonVariant.Text,
                    leadingIcon = Lucide.Download,
                )
            }
        }
        if (approval.approvals.isNotEmpty()) {
            Section(stringResource(Res.string.rmp_approval_history))
            approval.approvals.forEach { entry -> HistoryEntry(entry) }
        }
        RmpAdminActions(approval, controller, onChanged = onDecided)
        if (approval.canEdit || approval.canDelete) {
            HorizontalDivider()
            if (confirmingDelete) {
                Text(
                    text = stringResource(Res.string.rmp_approval_delete_confirm, item.code),
                    style = MaterialTheme.typography.bodyMedium,
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
            ) {
                if (confirmingDelete) {
                    AppButton(
                        text = stringResource(Res.string.rmp_decision_cancel),
                        onClick = { confirmingDelete = false },
                        enabled = !controller.deciding,
                        variant = PortalButtonVariant.Text,
                    )
                    AppButton(
                        text = stringResource(Res.string.rmp_approval_delete),
                        onClick = {
                            controller.delete {
                                onDismiss()
                                onDecided()
                            }
                        },
                        enabled = !controller.deciding,
                        variant = PortalButtonVariant.Filled,
                        leadingIcon = Lucide.Trash2,
                    )
                } else {
                    if (approval.canDelete) {
                        AppButton(
                            text = stringResource(Res.string.rmp_approval_delete),
                            onClick = { confirmingDelete = true },
                            enabled = !controller.deciding,
                            variant = PortalButtonVariant.Text,
                            leadingIcon = Lucide.Trash2,
                        )
                    }
                    if (approval.canEdit) {
                        AppButton(
                            text = stringResource(Res.string.rmp_approval_edit),
                            onClick = { onEdit(item.id) },
                            enabled = !controller.deciding,
                            variant = PortalButtonVariant.Outlined,
                            leadingIcon = Lucide.Pencil,
                        )
                    }
                }
            }
        }
        if (approval.canDecide) {
            HorizontalDivider()
            AppTextField(
                value = controller.note,
                onValueChange = controller::updateNote,
                label = stringResource(Res.string.rmp_approval_note),
                singleLine = false,
                enabled = !controller.deciding,
                modifier = Modifier.fillMaxWidth(),
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.rmp_approval_reject),
                    onClick = { controller.reject(onDecided) },
                    enabled = !controller.deciding,
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.X,
                )
                AppButton(
                    text = stringResource(Res.string.rmp_approval_approve),
                    onClick = { controller.approve(onDecided) },
                    enabled = !controller.deciding && approval.missingFields.isEmpty(),
                    variant = PortalButtonVariant.Filled,
                    leadingIcon = Lucide.Check,
                )
            }
        }
    }
}

/**
 * Aviso de obrigatórios vazios (ex.: a carta removida depois do envio): a etapa só é aprovada depois de incluídos.
 * Quem pode editar o RMP vê o botão "Anexar documentos", que abre o formulário ([onAttach]).
 */
@Composable
private fun MissingFieldsNotice(
    approval: RmpApproval,
    onAttach: () -> Unit,
) {
    if (approval.missingFields.isEmpty()) return
    val missing = stringResource(Res.string.rmp_missing_fields, approval.missingFields.joinToString(", "))
    val hint =
        stringResource(if (approval.canEdit) Res.string.rmp_missing_fields_edit else Res.string.rmp_missing_fields_ask)
    Text(
        text = "$missing $hint",
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.error,
    )
    if (approval.canEdit) {
        AppButton(
            text = stringResource(Res.string.rmp_attach_documents),
            onClick = onAttach,
            variant = PortalButtonVariant.Filled,
            leadingIcon = Lucide.Upload,
        )
    }
}

@Composable
private fun Summary(
    item: RmpOverviewItem,
    approval: RmpApproval,
) {
    val request = approval.request
    DetailRow(stringResource(Res.string.rmp_col_type), request.typeLabel ?: item.typeLabel ?: NO_VALUE)
    DetailRow(stringResource(Res.string.rmp_col_status), item.statusLabel ?: request.status)
    DetailRow(stringResource(Res.string.rmp_col_requester), approval.requesterName ?: item.requesterName ?: NO_VALUE)
    DetailRow(stringResource(Res.string.rmp_col_unit), request.companyName ?: NO_VALUE)
    DetailRow(
        stringResource(Res.string.rmp_col_waiting),
        listOfNotNull(approval.waitingStageLabel, approval.waitingName).joinToString(" · ").ifBlank { NO_VALUE },
    )
    RequiredActions(item, approval.waitingStageLabel, approval.waitingName, approval.canDecide)
}

/**
 * "Ações necessárias": o que precisa ser feito na etapa atual (texto da API) e quem deve fazer, com "(você)" quando
 * é o usuário logado. Não aparece quando o RMP não aguarda ninguém.
 */
@Composable
internal fun RequiredActions(
    item: RmpOverviewItem,
    stageLabel: String? = item.waitingStageLabel,
    waitingName: String? = item.waitingName,
    isMine: Boolean = item.myPending,
) {
    val action = item.requiredAction ?: return
    Section(stringResource(Res.string.rmp_required_actions))
    Text(text = action, style = MaterialTheme.typography.bodyMedium)
    // Sem nome próprio (ex.: CEO VV nos RMPs do portal) o nome é a própria etapa: mostra uma vez só.
    val who = listOfNotNull(stageLabel, waitingName).distinct().joinToString(" · ")
    if (who.isNotBlank()) {
        val you = if (isMine) " " + stringResource(Res.string.rmp_required_actions_you) else ""
        Text(
            text = stringResource(Res.string.rmp_required_actions_who, who) + you,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.primary,
        )
    }
}

@Composable
private fun Section(title: String) {
    HorizontalDivider()
    Text(text = title, style = MaterialTheme.typography.titleMedium)
}

@Composable
private fun HistoryEntry(entry: RmpApprovalEntry) {
    val decision =
        stringResource(
            if (entry.decision == "REJEITADO") Res.string.rmp_approval_rejected else Res.string.rmp_approval_approved,
        )
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = listOfNotNull(entry.stageLabel, decision).joinToString(" · "),
            style = MaterialTheme.typography.labelLarge,
        )
        Text(
            text = listOfNotNull(entry.decidedByName, formatBusinessDateTime(entry.decidedAt)).joinToString(" · "),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        entry.note?.let { note ->
            Text(text = note, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

/**
 * Confirmação de Aprovar/Rejeitar aberta direto da linha da tabela, com a observação/motivo opcional. A API decide
 * a etapa atual; [onDecided] fecha a confirmação e recarrega a lista.
 */
@Composable
internal fun RmpDecisionDialog(
    item: RmpOverviewItem,
    approve: Boolean,
    onDismiss: () -> Unit,
    onDecided: () -> Unit,
    onEdit: (String) -> Unit,
) {
    val controller = rememberRmpApprovalController()
    LaunchedEffect(item.id) {
        // Aprovar carrega o detalhe para avisar dos obrigatórios que faltam (ex.: um anexo removido).
        if (approve) controller.load(item.id) else controller.prepare(item.id)
    }
    AppModalCard(
        title =
            stringResource(
                if (approve) Res.string.rmp_decision_approve_title else Res.string.rmp_decision_reject_title,
                item.code,
            ),
        onDismiss = onDismiss,
    ) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        Text(
            text =
                stringResource(
                    Res.string.rmp_decision_summary,
                    item.typeLabel ?: item.type.orEmpty(),
                    item.unitName ?: NO_VALUE,
                    item.waitingStageLabel ?: NO_VALUE,
                ),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        val missing =
            approve &&
                controller.approval
                    ?.missingFields
                    .orEmpty()
                    .isNotEmpty()
        controller.approval?.takeIf { approve }?.let { approval ->
            MissingFieldsNotice(approval, onAttach = { onEdit(item.id) })
        }
        AppTextField(
            value = controller.note,
            onValueChange = controller::updateNote,
            label = stringResource(Res.string.rmp_approval_note),
            singleLine = false,
            enabled = !controller.deciding,
            modifier = Modifier.fillMaxWidth(),
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.rmp_decision_cancel),
                onClick = onDismiss,
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(if (approve) Res.string.rmp_approval_approve else Res.string.rmp_approval_reject),
                onClick = { if (approve) controller.approve(onDecided) else controller.reject(onDecided) },
                enabled = !controller.deciding && !controller.loading && !missing,
                variant = if (approve) PortalButtonVariant.Filled else PortalButtonVariant.Outlined,
                leadingIcon = if (approve) Lucide.Check else Lucide.X,
            )
        }
    }
}
